/**
 * Opaque keyset cursors (engine.md §9): base64url of a small JSON object. No OFFSET anywhere.
 * Self-contained base64url (no Buffer / btoa), so it behaves the same in a worker and in Node.
 */

import { DigesterError } from '../../kernel/errors.js';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const INDEX = new Map([...ALPHABET].map((c, i) => [c, i]));

/** UTF-8 bytes via encodeURIComponent (ES only: no TextEncoder in core's lib). */
function utf8(s: string): Uint8Array {
  const out: number[] = [];
  const escaped = encodeURIComponent(s);
  for (let i = 0; i < escaped.length; i++) {
    if (escaped[i] === '%') {
      out.push(parseInt(escaped.slice(i + 1, i + 3), 16));
      i += 2;
    } else {
      out.push(escaped.charCodeAt(i));
    }
  }
  return Uint8Array.from(out);
}

/** Inverse of utf8; throws URIError on malformed input. */
function fromUtf8(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += `%${b.toString(16).padStart(2, '0')}`;
  return decodeURIComponent(s);
}

export function base64url(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i] ?? 0;
    const b = bytes[i + 1];
    const c = bytes[i + 2];
    const n = (a << 16) | ((b ?? 0) << 8) | (c ?? 0);
    out += ALPHABET[(n >> 18) & 63]! + ALPHABET[(n >> 12) & 63]!;
    if (b !== undefined) out += ALPHABET[(n >> 6) & 63]!;
    if (c !== undefined) out += ALPHABET[n & 63]!;
  }
  return out;
}

export function fromBase64url(s: string): Uint8Array | null {
  const out: number[] = [];
  let buffer = 0;
  let bits = 0;
  for (const ch of s) {
    const v = INDEX.get(ch);
    if (v === undefined) return null;
    buffer = (buffer << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out.push((buffer >> bits) & 255);
      buffer &= (1 << bits) - 1;
    }
  }
  return Uint8Array.from(out);
}

export function encodeCursor(value: Record<string, unknown>): string {
  return base64url(utf8(JSON.stringify(value)));
}

/** Decode a cursor made by encodeCursor; anything else rejects invalid_input. */
export function decodeCursor(cursor: string): Record<string, unknown> {
  const bytes = fromBase64url(cursor);
  if (bytes) {
    try {
      const parsed: unknown = JSON.parse(fromUtf8(bytes));
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) return parsed as Record<string, unknown>;
    } catch {
      // fall through to the typed error
    }
  }
  throw new DigesterError('invalid_input', 'The page cursor is not valid.', { issues: [{ path: 'cursor', message: 'Not a cursor from this list' }] });
}

/** A cursor field that must be a finite number. */
export function cursorNumber(c: Record<string, unknown>, key: string): number {
  const v = c[key];
  if (typeof v !== 'number' || !Number.isFinite(v)) throw new DigesterError('invalid_input', 'The page cursor is not valid.', { issues: [{ path: 'cursor', message: `Missing ${key}` }] });
  return v;
}

/** A cursor field that must be a string. */
export function cursorString(c: Record<string, unknown>, key: string): string {
  const v = c[key];
  if (typeof v !== 'string') throw new DigesterError('invalid_input', 'The page cursor is not valid.', { issues: [{ path: 'cursor', message: `Missing ${key}` }] });
  return v;
}
